const LINE_COLORS = {
  OK: "#0000FF",
  ABOVE: "#FF0000",
  BELOW: "#FFFF00",
  OUT: "#FFFFFF",
};

export function pointStatusForPlannedSpeed(speed, plannedSpeed) {
  const value = Number(speed) || 0;
  if (value <= 0.5) return "BELOW";
  if (value > plannedSpeed * 1.1) return "ABOVE";
  return "OK";
}

export function parseSpeedKmh(rawSpeed) {
  return Number(String(rawSpeed || "").split(" km/h")[0]) || 0;
}

/** Série do gráfico (time ms + velocidade) a partir das linhas da API. */
export function buildGraphSeriesFromLines(lines) {
  const seen = new Set();
  const points = [];

  (lines || []).forEach((line) => {
    (line.path || []).forEach((point) => {
      const key = point.id != null ? String(point.id) : String(point.time);
      if (seen.has(key)) return;
      seen.add(key);
      points.push({
        velocidade: parseSpeedKmh(point.speed),
        time: new Date(point.time).getTime(),
        timestamp: point.time,
      });
    });
  });

  return points.sort((a, b) => a.time - b.time);
}

/** Extrai pontos únicos ordenados por hora a partir das linhas da API. */
export function extractRawPathPoints(lines) {
  const seen = new Set();
  const points = [];

  (lines || []).forEach((line) => {
    (line.path || []).forEach((point) => {
      if (!isValidTrackingCoordinate(point)) return;
      const key = point.id != null ? String(point.id) : String(point.time);
      if (seen.has(key)) return;
      seen.add(key);
      points.push({
        id: point.id,
        lat: point.lat,
        lng: point.lng,
        time: point.time,
        speed: parseSpeedKmh(point.speed),
        course: point.course || 0,
        inside_landplot: Boolean(point.inside_landplot),
        distance: point.distance || 0,
      });
    });
  });

  return points.sort(
    (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime()
  );
}

function appendPathPoint(path, point, rawPoint) {
  let distance = rawPoint.distance || 0;
  let show = false;
  let io = "";

  if (path.length) {
    distance += path[path.length - 1].distance;
    if (distance > 250) {
      show = true;
      distance = 0;
    }
    if (rawPoint.inside_landplot !== path[path.length - 1].inside_landplot) {
      io = rawPoint.inside_landplot ? "in" : "out";
    }
  }

  path.push({
    lat: rawPoint.lat,
    lng: rawPoint.lng,
    id: rawPoint.id,
    speed: `${Number(rawPoint.speed || 0).toFixed(1)} km/h`,
    distance,
    show,
    time: String(rawPoint.time),
    course: rawPoint.course,
    inside_landplot: rawPoint.inside_landplot,
    io,
  });
}

/** Recalcula segmentos coloridos do mapa conforme a velocidade de referência. */
export function rebuildTrackingLines(rawPoints, plannedSpeed) {
  if (!rawPoints || !rawPoints.length) return [];

  const lines = [];
  let lastPoint = rawPoints[0];
  let pointStatus = pointStatusForPlannedSpeed(lastPoint.speed, plannedSpeed);

  lines.push({
    status: pointStatus,
    color: LINE_COLORS[pointStatus],
    path: [],
  });
  appendPathPoint(lines[lines.length - 1].path, lastPoint, lastPoint);

  for (let i = 1; i < rawPoints.length; i += 1) {
    const point = rawPoints[i];
    const previousStatus = pointStatus;
    pointStatus = pointStatusForPlannedSpeed(point.speed, plannedSpeed);

    if (pointStatus !== previousStatus) {
      lines.push({
        status: pointStatus,
        color: LINE_COLORS[pointStatus],
        path: [],
      });
      appendPathPoint(lines[lines.length - 1].path, lastPoint, lastPoint);
    }

    appendPathPoint(lines[lines.length - 1].path, point, point);
    lastPoint = point;
  }

  return lines.filter((line) => line.path.length > 0);
}

export function stoppedPointsFromLines(lines) {
  const stops = [];
  (lines || []).forEach((line) => {
    (line.path || []).forEach((point) => {
      if (point.show) {
        stops.push({
          lat: point.lat,
          lng: point.lng,
          time: point.time,
        });
      }
    });
  });
  return stops;
}

/** Rejeita nulo, NaN e o ponto zero (0,0) que a API usa quando não há GPS. */
export function isValidTrackingCoordinate(point) {
  if (!point) return false;
  const lat = Number(point.lat);
  const lng = Number(point.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (Math.abs(lat) < 0.000001 && Math.abs(lng) < 0.000001) return false;
  return Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
}

/** Último ponto com coordenada válida, do fim da trilha para o início. */
export function lastValidPointFromLines(lines) {
  const points = extractRawPathPoints(lines);
  for (let index = points.length - 1; index >= 0; index -= 1) {
    if (isValidTrackingCoordinate(points[index])) {
      return points[index];
    }
  }
  return null;
}

/** Ícone Traccar padrão para rastreamento de operadores (GPS do app de campo). */
export const OPERATOR_TRACKING_MAP_ICON = "person";

export function resolveOperatorMapEquipment(equipment, liveEquipment) {
  const base = equipment || {
    ident: "Operador",
    machine_status: "online",
    traccar_ico: OPERATOR_TRACKING_MAP_ICON,
  };

  return {
    ...base,
    traccar_ico: OPERATOR_TRACKING_MAP_ICON,
    machine_status:
      (liveEquipment && liveEquipment.machine_status) ||
      base.machine_status ||
      "online",
  };
}
